import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { SeasonProvider } from './context/SeasonContext';
import StoreLayout from './components/layouts/StoreLayout';
import AdminLayout from './components/layouts/AdminLayout';
import EmailPopup from './components/EmailPopup';
import ExitIntentPopup from './components/ExitIntentPopup';
import LiveChat from './components/LiveChat';
import FlashSaleTimer from './components/FlashSaleTimer';
import AishaChatWidget from './components/AishaChatWidget';
import AishaSmartFinder from './components/AishaSmartFinder';
import { useReferralTracking } from './lib/referral';
import { publicRoutes, protectedRoutes, adminRoutes, adminSpecialRoutes } from './config/routes';
import { loadTrackingTags } from './lib/trackingLoader';

import './App.css';

// Loading fallback for lazy-loaded components
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50">
    <div className="text-center">
      <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
      <p className="text-slate-600">Loading...</p>
    </div>
  </div>
);

// Protected Route for Admin
const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (!user || !isAdmin) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Protected Route for Auth
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Component to track referrals
const ReferralTracker = ({ children }) => {
  useReferralTracking();
  return children;
};

// Render a route element with appropriate layout
const RouteElement = ({ route }) => {
  const Component = route.element;
  
  // Component is already a component instance (not lazy)
  if (route.isComponent) {
    if (route.layout === 'store') {
      return <StoreLayout><Component /></StoreLayout>;
    }
    return <Component />;
  }
  
  // Lazy loaded component
  if (route.layout === 'store') {
    return (
      <StoreLayout>
        <Suspense fallback={<PageLoader />}>
          <Component />
        </Suspense>
      </StoreLayout>
    );
  }
  
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
};

// Render admin route with layout
const AdminRouteElement = ({ route }) => {
  const Component = route.element;
  return (
    <AdminRoute>
      <AdminLayout>
        <Suspense fallback={<PageLoader />}>
          <Component />
        </Suspense>
      </AdminLayout>
    </AdminRoute>
  );
};

function AppRoutes() {
  return (
    <ReferralTracker>
      <Routes>
        {/* Public Store Routes */}
        {publicRoutes.map((route) => (
          <Route 
            key={route.path} 
            path={route.path} 
            element={<RouteElement route={route} />} 
          />
        ))}
        
        {/* Protected Routes (require auth) */}
        {protectedRoutes.map((route) => (
          <Route 
            key={route.path} 
            path={route.path} 
            element={
              <ProtectedRoute>
                <RouteElement route={route} />
              </ProtectedRoute>
            } 
          />
        ))}
        
        {/* Admin Routes (require admin auth) */}
        {adminRoutes.map((route) => (
          <Route 
            key={route.path} 
            path={route.path} 
            element={<AdminRouteElement route={route} />} 
          />
        ))}
        
        {/* Admin Special Routes (no admin layout) */}
        {adminSpecialRoutes.map((route) => {
          const Component = route.element;
          return (
            <Route 
              key={route.path} 
              path={route.path} 
              element={
                <Suspense fallback={<PageLoader />}>
                  <Component />
                </Suspense>
              } 
            />
          );
        })}

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ReferralTracker>
  );
}

// Conditionally render A.I_SHA widgets (hide on admin and immersive routes)
const AishaWidgets = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isImmersiveRoute = location.pathname === '/ship';
  
  if (isAdminRoute || isImmersiveRoute) return null;
  
  return (
    <>
      <AishaChatWidget />
      <AishaSmartFinder />
    </>
  );
};

// Conditionally render global overlays (hide on immersive routes)
const GlobalOverlays = () => {
  const location = useLocation();
  const isImmersiveRoute = ['/ship', '/', '/voyage', '/selah', '/library'].includes(location.pathname);
  
  if (isImmersiveRoute) return null;
  
  return (
    <>
      <FlashSaleTimer discount={20} />
      <EmailPopup />
      <ExitIntentPopup />
      <LiveChat />
    </>
  );
};

function App() {
  useEffect(() => { loadTrackingTags(); }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <SeasonProvider>
            <GlobalOverlays />
            <AppRoutes />
            <AishaWidgets />
            <Toaster position="top-right" richColors closeButton />
          </SeasonProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
